import React from 'react';
import { Wrench, Edit, Trash2, Store, Lock, Sparkles, Tag } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';
import itemService from '../../services/itemService';
import { getCategoryDetails } from './CategoryPickerModal';

const ServiceDetailsView = ({ item, onEdit, onClose, onRefresh }) => {
  const { t } = useLang();

  if (!item) return null;

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return parseFloat(num).toFixed(2);
  };

  const handleDelete = async () => {
    if (window.confirm(t(LangKeys.delete_confirmation) || "Are you sure you want to delete this service?")) {
      const success = await itemService.deleteItem(item.id);
      if (success) {
        onClose();
        if (onRefresh) onRefresh();
      }
    }
  };

  const isOnline = item.is_public !== 0 && item.is_public !== false && item.is_public !== '0';
  const catInfo = getCategoryDetails(item.category, true, t);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <p style={styles.label}>{t(LangKeys.service_name)}</p>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  backgroundColor: isOnline ? '#F3E8FF' : '#F1F5F9',
                  color: isOnline ? '#7E22CE' : '#64748B',
                  border: `1px solid ${isOnline ? '#E9D5FF' : '#E2E8F0'}`
                }}
              >
                {isOnline ? <Store size={12} /> : <Lock size={12} />}
                {isOnline ? (t(LangKeys.online_active) || 'Online in Market') : (t(LangKeys.online_private) || 'Private')}
              </span>
            </div>
            <h2 style={styles.itemName}>{item.name}</h2>
          </div>
          <div style={styles.iconBox}>
            {(item.photo_url || item.image_path || item.image) ? (
              <img 
                src={item.photo_url || item.image_path || item.image} 
                alt={item.name} 
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }} 
              />
            ) : (
              <Wrench size={32} color="#9333EA" />
            )}
          </div>
        </div>

        {/* Marketplace & Service Category Banner */}
        <div style={{
          backgroundColor: '#FFF',
          padding: '12px 16px',
          borderRadius: '14px',
          border: '1px solid #E2E8F0',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>{catInfo.icon}</span>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase' }}>
                {t(LangKeys.item_category_lbl) || 'Service Category'}
              </span>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>
                {catInfo.title} <span style={{ fontSize: '12px', fontWeight: '400', color: '#64748B' }}>({catInfo.subtitle})</span>
              </div>
            </div>
          </div>
          {parseFloat(item.discount_price || 0) > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#FAF5FF',
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #E9D5FF'
            }}>
              <Tag size={14} color="#9333EA" />
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#9333EA' }}>
                Offer Price: ₹{formatNumber(item.discount_price)}
              </span>
            </div>
          )}
        </div>

        {/* Optional Service Description / SEO */}
        {item.description && (
          <div style={{
            backgroundColor: '#FAF5FF',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid #F3E8FF',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <Sparkles size={12} color="#9333EA" />
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B' }}>
                {t(LangKeys.service_desc_lbl) || 'Service Details / SEO'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: '1.4' }}>
              {item.description}
            </p>
          </div>
        )}

        {/* Grid Details */}
        <div style={styles.grid}>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.service_price)}</p>
            <p style={styles.value}>₹{formatNumber(item.sale_price)}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.primary_unit)}</p>
            <p style={styles.value}>{item.unit || 'NOS'}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.sac_code)}</p>
            <p style={styles.value}>{item.hsn_sac_code || '-'}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.gst_rate_percent)}</p>
            <p style={styles.value}>{item.gst_rate ? `${item.gst_rate}%` : '-'}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={styles.actions}>
           <button style={styles.editBtn} onClick={() => onEdit(item)}>
             <Edit size={18} /> {t(LangKeys.edit_service)}
           </button>
           <button style={styles.deleteBtn} onClick={handleDelete}>
             <Trash2 size={18} />
           </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#F8FAFC' },
  content: { flex: 1, padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  titleSection: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: '700', color: '#94A3B8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' },
  itemName: { fontSize: '28px', fontWeight: '900', color: '#0F172A', margin: 0 },
  iconBox: { width: '70px', height: '70px', backgroundColor: '#F5F3FF', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E9E3FF', overflow: 'hidden' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#FFF' },
  cell: { padding: '20px', borderRight: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '8px' },
  value: { fontSize: '18px', fontWeight: '800', color: '#1E293B', margin: 0 },
  actions: { display: 'flex', gap: '12px', marginTop: '32px', justifyContent: 'flex-end' },
  editBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '12px', color: '#2563EB', fontWeight: '700', cursor: 'pointer' },
  deleteBtn: { padding: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FEE2E2', borderRadius: '12px', color: '#DC2626', cursor: 'pointer' },
};

export default ServiceDetailsView;
